import React, { useState, useEffect } from 'react';
import { apiCall } from '../config/api';
import { ToastContext } from '../App';

interface WeatherData {
  current?: {
    temp: number;
    humidity: number;
    wind_speed: number;
    weather: Array<{
      main: string;
      description: string;
      icon: string;
    }>;
  };
  daily?: Array<{
    dt: number;
    temp: {
      min: number;
      max: number;
    };
    weather: Array<{
      main: string;
      description: string;
      icon: string;
    }>;
  }>;
  hourly?: Array<{
    dt: number;
    temp: number;
    weather: Array<{
      main: string;
      description: string;
      icon: string;
    }>;
  }>;
  forecast?: Array<{
    date: string;
    day_name: string;
    temp_min: number;
    temp_max: number;
    temp_avg: number;
    humidity_avg: number;
    wind_speed_avg?: number;
    clouds_avg?: number;
    description: string;
    icon: string;
  }>;
  hourly_data?: Array<{
    datetime: string;
    time: string;
    date: string;
    temp: number;
    humidity: number;
    description: string;
    icon: string;
    wind_speed: number;
  }>;
  location?: string;
  last_updated?: string;
  error?: string;
  message?: string;
}

const WeatherPage: React.FC = () => {
  const [weatherData, setWeatherData] = useState<WeatherData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = React.useContext(ToastContext);

  useEffect(() => {
    loadWeatherForecast();
  }, []);

  const loadWeatherForecast = async () => {
    setLoading(true);
    setError('');
    
    try {
      const data = await apiCall('/weather/forecast?days=5', { method: 'GET' });
      
      if (data.error) {
        setError(data.message || data.error);
        setWeatherData(null);
        showToast('Erro ao carregar previsão do tempo', 'error');
      } else {
        setWeatherData(data);
        setError('');
        showToast('Previsão do tempo carregada com sucesso!', 'success');
      }
    } catch (err) {
      setError('Erro ao comunicar com o servidor');
      setWeatherData(null);
      showToast('Erro ao comunicar com o servidor', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getWeatherIconClass = (iconCode: string) => {
    const iconMap: { [key: string]: string } = {
      '01d': 'fas fa-sun text-warning',
      '01n': 'fas fa-moon text-secondary',
      '02d': 'fas fa-cloud-sun text-warning',
      '02n': 'fas fa-cloud-moon text-secondary',
      '03d': 'fas fa-cloud text-secondary',
      '03n': 'fas fa-cloud text-secondary',
      '04d': 'fas fa-cloud text-secondary',
      '04n': 'fas fa-cloud text-secondary',
      '09d': 'fas fa-cloud-rain text-info',
      '09n': 'fas fa-cloud-rain text-info',
      '10d': 'fas fa-cloud-sun-rain text-info',
      '10n': 'fas fa-cloud-moon-rain text-info',
      '11d': 'fas fa-bolt text-warning',
      '11n': 'fas fa-bolt text-warning',
      '13d': 'fas fa-snowflake text-info',
      '13n': 'fas fa-snowflake text-info',
      '50d': 'fas fa-smog text-secondary',
      '50n': 'fas fa-smog text-secondary'
    };
    return iconMap[iconCode] || 'fas fa-question text-muted';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="row">
          <div className="col-12">
            <div className="card shadow">
              <div className="card-header" style={{ backgroundColor: '#a2449c', color: 'white' }}>
                <h5><i className="fas fa-cloud-sun me-2"></i>Previsão do Tempo - Seixal, Portugal</h5>
              </div>
              <div className="card-body">
                <div className="text-center py-4">
                  <div className="spinner-border" style={{ color: '#a2449c' }} role="status">
                    <span className="visually-hidden">A carregar...</span>
                  </div>
                  <p className="mt-2 text-muted">A carregar previsão do tempo...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow">
            <div className="card-header mb-3" style={{ backgroundColor: '#a2449c', color: 'white' }}>
              <h5><i className="fas fa-cloud-sun me-2"></i>Previsão do Tempo - Seixal, Portugal</h5>
            </div>
            <div className="card-body">
              {/* Bloco de informação + botão responsivo */}
              <div className="row mb-4 g-2 flex-column flex-md-row align-items-stretch align-items-md-center">
                <div className="col-12 col-md-8 d-flex align-items-center mb-2 mb-md-0">
                  <div className="alert alert-info w-100 mb-0 d-flex align-items-center" style={{ minHeight: 56 }}>
                    <i className="fas fa-info-circle me-2"></i>
                    <span><strong>Previsão a 5 dias</strong> para Seixal, Portugal</span>
                  </div>
                </div>
                <div className="col-12 col-md-4 d-flex align-items-center justify-content-md-end justify-content-center">
                  <button 
                    type="button" 
                    className="btn fw-bold w-100 w-md-auto px-4 py-2"
                    style={{ minWidth: 180, backgroundColor: '#a2449c', color: 'white', border: 'none' }}
                    onClick={loadWeatherForecast}
                    disabled={loading}
                  >
                    <i className="fas fa-sync-alt me-1"></i>Atualizar Previsão
                  </button>
                </div>
              </div>

              {/* Removido bloco de erro direto, manter apenas Toast */}

              {weatherData && !weatherData.error && weatherData.forecast && (
                <>
                  {/* Current Weather */}
                  {weatherData.current && (
                    <div className="mb-4">
                      <div className="card bg-light">
                        <div className="card-body">
                          <div className="row align-items-center">
                            <div className="col-md-8">
                              <h6 className="mb-1">Tempo Atual</h6>
                              <p className="mb-0 text-muted">{weatherData.location || 'Seixal, Portugal'}</p>
                            </div>
                            <div className="col-md-4 text-end">
                              <div className="d-flex align-items-center justify-content-end">
                                <i className={`${getWeatherIconClass(weatherData.current.weather[0].icon)} fs-1 me-3`}></i>
                                <div>
                                  <div className="fs-4 fw-bold">{Math.round(weatherData.current.temp)}°C</div>
                                  <div className="text-muted">{weatherData.current.weather[0].description}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Weather Forecast */}
                  <div>
                    <div className="row" id="forecastCards">
                      {weatherData.forecast.map((day, index) => (
                        <div key={index} className="col-12 mb-4">
                          <div className="card border-0 shadow-sm">
                            {/* Header do dia */}
                            <div className="card-header" style={{ backgroundColor: '#f3d9fa', color: '#4a3c0a' }}>
                              <div className="row align-items-center">
                                <div className="col-md-6">
                                  <h5 className="mb-0">
                                    <i className="fas fa-calendar-day me-2"></i>
                                    {day.day_name} - {formatDate(day.date)}
                                  </h5>
                                </div>
                                <div className="col-md-6 text-end">
                                  <div className="d-flex align-items-center justify-content-end">
                                    <i className={`${getWeatherIconClass(day.icon)} fs-2 me-3`}></i>
                                    <div>
                                      <div className="fs-4 fw-bold">{day.temp_avg}°C</div>
                                      <div className="small">{day.description}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            <div className="card-body p-0">
                              {/* Informação geral do dia */}
                              <div className="p-3 bg-light border-bottom">
                                <div className="row text-center">
                                  <div className="col-md-2 col-4 mb-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body py-2">
                                        <div className="text-danger fw-bold">{day.temp_max}°C</div>
                                        <div className="small text-muted">Máxima</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2 col-4 mb-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body py-2">
                                        <div className="text-info fw-bold">{day.temp_min}°C</div>
                                        <div className="small text-muted">Mínima</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2 col-4 mb-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body py-2">
                                        <div className="text-primary fw-bold">{day.temp_avg}°C</div>
                                        <div className="small text-muted">Média</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2 col-4 mb-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body py-2">
                                        <div className="text-success fw-bold">{day.humidity_avg}%</div>
                                        <div className="small text-muted">Humidade</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2 col-4 mb-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body py-2">
                                        <div className="text-warning fw-bold">{day.wind_speed_avg || 0} km/h</div>
                                        <div className="small text-muted">Vento</div>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="col-md-2 col-4 mb-2">
                                    <div className="card border-0 bg-white">
                                      <div className="card-body py-2">
                                        <div className="text-secondary fw-bold">{day.clouds_avg || 0}%</div>
                                        <div className="small text-muted">Nuvens</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              {/* Dados por hora para este dia */}
                              {weatherData.hourly_data && (
                                <div className="p-3">
                                  <h6 className="mb-3 text-muted">
                                    <i className="fas fa-clock me-2"></i>Previsão por Hora
                                  </h6>
                                  <div className="row">
                                    {weatherData.hourly_data
                                      .filter(hour => hour.date === day.date)
                                      .slice(0, 8)
                                      .map((hour, hourIndex) => (
                                        <div key={hourIndex} className="col-lg-2 col-md-3 col-sm-4 col-6 mb-2">
                                          <div className="text-center p-2 border rounded bg-light h-100">
                                            <div className="small fw-bold text-primary mb-1" style={{ color: '#a2449c' }}>{hour.time}</div>
                                            <div className="mb-2">
                                              <i className={`${getWeatherIconClass(hour.icon)} fs-4`}></i>
                                            </div>
                                            <div className="fw-bold fs-5 mb-1">{hour.temp}°C</div>
                                            <div className="small text-muted mb-1">{hour.description}</div>
                                            <div className="small text-info mb-1">
                                              <i className="fas fa-tint me-1"></i>{hour.humidity}%
                                            </div>
                                            <div className="small text-secondary">
                                              <i className="fas fa-wind me-1"></i>{hour.wind_speed} km/h
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {weatherData && weatherData.last_updated && (
                <div className="mt-3 text-center text-muted">
                  <small>Última atualização: {weatherData.last_updated}</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeatherPage; 